import classnames from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';

import styles from './styles.module.scss';
import type { InferPropTypes } from '../../typings/index.ts';
import addVariants from '../../utils/addVariants/index.ts';
import useSpacing from '../../utils/useSpacing/index.ts';

export const VARIANTS = ['default'] as const;
export type ColVariant = (typeof VARIANTS)[number];
export const ALIGNMENTS = ['start', 'end', 'center', 'baseline', 'stretch'] as const;
export const JUSTIFICATIONS = ['start', 'end', 'center', 'space-around', 'space-between'] as const;

const propTypes = {
  /**
   * Any children to be rendered
   */
  children: PropTypes.node.isRequired,
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Decides the variant
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf<ColVariant>(VARIANTS).isRequired,
    PropTypes.bool.isRequired,
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.oneOf<ColVariant>(VARIANTS).isRequired,
        PropTypes.bool.isRequired
      ]).isRequired
    )
  ]),
  /**
   * The width of the `Col`:
   * - if a _number_ (1-16) corresponds to the number of columns spanned in a 16 column grid system
   * - if a _string_ will use that as the width as CSS styled inline
   * @default 'auto'
   * @example <caption>Example Usage</caption>
   * <Col width={8} /> // Will span 8 of 16 columns AKA 50%
   * <Col width={4} /> // Will span 4 of 16 columns AKA 25%
   * <Col width={'500px'} // Width is 500px
   */
  width: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]).isRequired,
    PropTypes.string
  ]),
  /**
   * Decides vertical aligning of the `Col`
   * Maps directly to CSS `align-self`
   */
  align: PropTypes.oneOf<(typeof ALIGNMENTS)[number]>(ALIGNMENTS),
  /**
   * Decides horizontal aligning of the `Col`
   * Maps directly to CSS `justify-content`
   */
  justify: PropTypes.oneOf<(typeof JUSTIFICATIONS)[number]>(JUSTIFICATIONS),
  /**
   * Allow customization of the padding within the `Col`. See `Spacer` Component
   */
  spacing: PropTypes.exact({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Any additional props to attach to the `Row`'s underlying `div`
   */
  containerProps: PropTypes.shape({})
};

const defaultProps = {
  className: '',
  variant: 'default',
  align: null,
  justify: null,
  spacing: {},
  style: {},
  containerProps: {},
  width: 'auto'
};

export interface ColOverrideProps {
  containerProps: React.HTMLAttributes<HTMLDivElement>;
  style: React.CSSProperties;
}

export type ColProps = InferPropTypes<typeof propTypes, typeof defaultProps, ColOverrideProps>;

/**
 * A `Col` is useful in conjunction with `Row` for creating horizontal or vertical layouts
 * It is essentially a ~flex item~.
 */
const Col: React.FC<ColProps> = ({
  variant,
  className,
  children,
  align,
  justify,
  width,
  spacing,
  style,
  containerProps
}: ColProps) => {
  const spaceStyles = useSpacing(spacing, {
    style: {
      width: typeof width !== 'number' ? width : undefined,
      ...style
    },
    margin: false
  });

  return (
    <div
      className={classnames(
        addVariants(
          'aglmd-col',
          styles,
          variant,
          `align-${align}`,
          `justify-${justify}`,
          typeof width === 'number' ? `width-${width}` : ''
        ),
        className
      )}
      style={spaceStyles}
      {...containerProps}
    >
      {children}
    </div>
  );
};

Col.propTypes = propTypes;
Col.defaultProps = defaultProps;

export default Col;
